import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-select-dropdown',
  templateUrl: './select-dropdown.component.html',
  styleUrl: './select-dropdown.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SelectDropdownComponent implements OnInit, OnChanges {
  private _cdr = inject(ChangeDetectorRef);

  @Input() options?: any[];
  @Input() control!: any;
  @Input() dropdownId: string = '';
  @Input() placeholder: string = 'Select...';
  @Input() enableTypeahead: boolean = true;
  @Output() ngModelChange = new EventEmitter<any>();

  @ViewChild('dropdown') dropdown?: NgbDropdown;
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('dropdownButton') dropdownButton?: ElementRef<HTMLDivElement>;

  menuItem: string = '';
  searchTerm: string = '';
  filteredOptions: any[] = [];
  focusedIndex: number = -1;
  pendingSearchChar: string = '';

  toggleTimeout: any = null;

  onSelect(item: any, event: Event) {
    event.preventDefault();
    event.stopPropagation();

    if (typeof item === 'string' || typeof item === 'number') {
      this.control.setValue(item);
    } else if (typeof item === 'object') {
      if ('abbreviation' in item) {
        this.control.setValue(item.abbreviation);
      } else if ('value' in item) {
        this.control.setValue(item.value);
      }
    }

    this.searchTerm = '';
    this.filteredOptions = this.options || [];
    this.focusedIndex = -1;
    this.dropdown?.close();

    setTimeout(() => {
      this.dropdownButton?.nativeElement.focus();
    }, 0);

    this.ngModelChange.emit(this.control.value);
  }

  isSelected(option: any): boolean {
    const currentValue = this.control.value;

    if (typeof option === 'string' || typeof option === 'number') {
      return currentValue === option;
    }

    if (typeof option === 'object') {
      if ('abbreviation' in option) {
        return currentValue === option.abbreviation;
      }
      if ('value' in option) {
        return currentValue === option.value;
      }
    }

    return false;
  }

  getDisplayValue(value: any): string {
    if (!this.options) {
      return value || '';
    }

    const isBooleanDropdown = this.options.some(
      (option) =>
        typeof option === 'object' &&
        'value' in option &&
        typeof option.value === 'boolean'
    );

    if (
      isBooleanDropdown &&
      (value === null || value === undefined || value === false)
    ) {
      const noOption = this.options.find(
        (option) =>
          typeof option === 'object' &&
          'value' in option &&
          option.value === false
      );
      return noOption?.label || 'No';
    }

    if (!value && value !== 0 && !isBooleanDropdown) {
      return '';
    }

    const matchingOption = this.options.find((option) => {
      if (typeof option === 'string' || typeof option === 'number') {
        return option === value;
      }
      if ('abbreviation' in option) {
        return option.abbreviation === value;
      }
      if ('value' in option) {
        return option.value === value;
      }
      return false;
    });

    if (!matchingOption) {
      return value || '';
    }

    if (
      typeof matchingOption === 'string' ||
      typeof matchingOption === 'number'
    ) {
      return String(matchingOption);
    }

    return matchingOption.name || matchingOption.label || String(value);
  }

  getOptionText(option: any): string {
    if (typeof option === 'string' || typeof option === 'number') {
      return String(option);
    }
    return option.name || option.label || '';
  }

  onSearchChange(searchTerm: string) {
    this.searchTerm = searchTerm;

    if (!searchTerm || !this.options) {
      this.filteredOptions = this.options || [];
      this.focusedIndex = -1;
      this._cdr.markForCheck();
      return;
    }

    const lowerSearch = searchTerm.toLowerCase();
    this.filteredOptions = this.options.filter((option) => {
      const text = this.getOptionText(option).toLowerCase();
      return text.includes(lowerSearch);
    });

    if (this.filteredOptions.length > 0) {
      this.focusedIndex = 0;
      this.scrollToFocusedItem();
    } else {
      this.focusedIndex = -1;
    }
    this._cdr.markForCheck();
  }

  onDropdownOpened() {
    this.focusedIndex = -1;

    if (this.enableTypeahead) {
      setTimeout(() => {
        if (this.searchInput) {
          this.searchInput.nativeElement.focus();
          if (this.pendingSearchChar) {
            this.searchTerm = this.pendingSearchChar;
            this.onSearchChange(this.searchTerm);
            this.searchInput.nativeElement.setSelectionRange(1, 1);
            this.pendingSearchChar = '';
          }
        }
      }, 100);
    }
  }

  onDropdownToggle(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.toggleTimeout) {
      return;
    }

    if (this.dropdown) {
      const isCurrentlyOpen = this.dropdown.isOpen();

      if (isCurrentlyOpen) {
        this.dropdown.close();
      } else {
        this.dropdown.open();
      }

      this.toggleTimeout = setTimeout(() => {
        this.toggleTimeout = null;
      }, 200);
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.dropdown?.isOpen()) {
      if (
        event.key === 'Enter' ||
        event.key === ' ' ||
        event.key === 'ArrowDown'
      ) {
        event.preventDefault();
        event.stopPropagation();
        this.dropdown?.open();
        return;
      }

      if (
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        this.pendingSearchChar = event.key;
        this.dropdown?.open();
        return;
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        if (this.filteredOptions.length > 0) {
          this.focusedIndex =
            (this.focusedIndex + 1) % this.filteredOptions.length;
          this.scrollToFocusedItem();
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        if (this.filteredOptions.length > 0) {
          this.focusedIndex =
            this.focusedIndex <= 0
              ? this.filteredOptions.length - 1
              : this.focusedIndex - 1;
          this.scrollToFocusedItem();
        }
        break;

      case 'Enter':
        event.preventDefault();
        event.stopPropagation();
        if (
          this.focusedIndex >= 0 &&
          this.focusedIndex < this.filteredOptions.length
        ) {
          this.onSelect(this.filteredOptions[this.focusedIndex], event);
        }
        break;

      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.dropdown.close();
        break;

      case 'Tab':
        this.dropdown.close();
        break;
    }
  }

  scrollToFocusedItem() {
    setTimeout(() => {
      const focusedElement = document.querySelector('.dropdown-item.focused');
      if (focusedElement) {
        try {
          focusedElement.scrollIntoView({
            block: 'nearest',
            behavior: 'smooth'
          });
        } catch (e) {
          focusedElement.scrollIntoView(false);
        }
      }
    }, 0);
  }

  isFocused(index: number): boolean {
    return this.focusedIndex === index;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options']) {
      this.filteredOptions = this.options || [];
      this._cdr.markForCheck();
    }
  }

  ngOnInit() {
    if (this.control instanceof FormControl) {
      this.control.valueChanges.subscribe({
        next: (value) => {
          this.menuItem = this.getDisplayValue(value);
          this._cdr.markForCheck();
        }
      });
    }

    this.menuItem = this.getDisplayValue(this.control.value);
    this.filteredOptions = this.options || [];
    this._cdr.markForCheck();
  }
}
